import {
  Block,
  BlockFooter,
  Button,
  List,
  ListInput,
  ListItem,
} from "framework7-react";
import { useEffect, useId, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useEnterKeys } from "../../hooks/use-enter-keys";
import { vibrate } from "../../hooks/use-haptics";
import { useToast } from "../../hooks/use-toast";
import {
  addMe,
  assignMe,
  getGroupStatus,
  selectCurrentGroup,
  selectMe,
} from "../../redux/reducer/app-slice";
import {
  duplicateNameMessage,
  isDuplicateName,
} from "../../utils/participant-names";
import PersonPickerPopup from "../person-picker-popup";
import { ASSIGN_HINT } from "./assign-prompt";

const NAME_INPUT = "me-name-input";

const AssignMePopup = ({ opened, welcome = false, onClose, onDone }) => {
  const dispatch = useDispatch();
  const { showError } = useToast();
  const group = useSelector(selectCurrentGroup);
  const me = useSelector(selectMe);
  const [isAdding, setIsAdding] = useState(false);
  const [name, setName] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const formId = `me-form-${useId().replace(/:/g, "")}`;
  useEnterKeys(formId, { [NAME_INPUT]: { hint: "go" } });

  useEffect(() => {
    if (isAdding) {
      document.querySelector(`#${formId} input[name=${NAME_INPUT}]`)?.focus();
    }
  }, [isAdding, formId]);

  const groupId = group?.id;

  const finish = () => {
    vibrate();
    onDone();
  };

  const fail = (message) => {
    showError(message);
    setIsBusy(false);
  };

  const assign = (participantId) => {
    if (isBusy) return;
    setIsBusy(true);
    dispatch(assignMe(groupId, participantId, finish, fail));
  };

  const isDuplicate = isDuplicateName(
    name,
    (group?.participants ?? []).map((participant) => participant.name),
  );

  const addMyself = (event) => {
    event.preventDefault();
    if (isBusy || isDuplicate) return;
    const trimmed = name.trim();
    if (!trimmed) {
      showError("Bitte gib deinen Namen ein.");
      return;
    }
    setIsBusy(true);
    dispatch(
      addMe(trimmed, groupId, finish, (message, { participantsSaved = false } = {}) => {
        if (participantsSaved) {
          dispatch(getGroupStatus(groupId, true));
          finish();
          return;
        }
        fail(message);
      }),
    );
  };

  const handleClosed = () => {
    setIsAdding(false);
    setName("");
    setIsBusy(false);
    onClose();
  };

  return (
    <PersonPickerPopup
      className="assign-me-popup"
      title={welcome ? group?.name : "Wer bist du?"}
      opened={opened}
      onClose={handleClosed}
      onSelect={assign}
      participants={group?.participants}
      selectedId={me?.id}
      intro={
        welcome && (
          <Block className="assign-me-welcome">
            Willkommen in {group?.name} – wer bist du?
          </Block>
        )
      }
    >
      <Block>
        <div id={formId}>
          {isAdding ? (
            <List
              className="assign-me-add"
              strong
              form
              formStore={false}
              onSubmit={addMyself}
            >
              <ListInput
                name={NAME_INPUT}
                outline
                type="text"
                label="Dein Name"
                floatingLabel
                placeholder="Dein Name"
                autocomplete="off"
                autocapitalize="words"
                value={name}
                onChange={(event) => setName(event.target.value)}
                errorMessage={
                  isDuplicate ? duplicateNameMessage(name) : undefined
                }
                errorMessageForce={isDuplicate}
              />
              <ListItem>
                <Button fill type="submit" disabled={isBusy || isDuplicate}>
                  Hinzufügen
                </Button>
              </ListItem>
            </List>
          ) : (
            <List strong>
              <ListItem
                link
                className="assign-me-not-listed"
                title="Ich stehe noch nicht auf der Liste"
                onClick={() => setIsAdding(true)}
              />
            </List>
          )}
        </div>
      </Block>
      {me && !welcome && (
        <Block>
          <Button
            tonal
            color="red"
            className="assign-me-clear"
            disabled={isBusy}
            onClick={() => assign(null)}
          >
            Zuweisung aufheben
          </Button>
        </Block>
      )}
      {welcome && (
        <Block>
          <Button tonal className="assign-me-skip" onClick={onDone}>
            Überspringen
          </Button>
          <BlockFooter className="assign-me-hint">{ASSIGN_HINT}</BlockFooter>
        </Block>
      )}
    </PersonPickerPopup>
  );
};

export default AssignMePopup;
